import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Pencil } from 'lucide-react';
import useAdicionalCatalogService, {
  ManagementItem,
  SuperintendencyItem,
  AreaItem,
  ElAbraGroupItem,
  AdicionalEvents,
} from '../../services/useAdicionalCatalogService';
import type { ServiceResponse } from '../../hooks/useFetch';
import showToast from '../../utils/toast';
import AdicionalCatalogModal from '../../components/Services/Adicional/AdicionalCatalogModal';
import ElAbraGroupModal from '../../components/Services/Adicional/ElAbraGroupModal';
import Pagination from '../../components/Base/Pagination';
import TableTextSearch from '../../components/Base/TableTextSearch';
import ADICIONAL_COMPANY_NAME from '../../config/adicionalCompany';
import './ElAbraCatalogPage.css';

type ElAbraModalKind = 'management' | 'superintendency' | 'area';
type ElAbraTabId = 'managements' | 'superintendencies' | 'areas' | 'groups';

const DEFAULT_PAGE_SIZE = 100;
const OPTIONS_PAGE_SIZE = 200;
const SEARCH_DEBOUNCE_MS = 400;

const TABS: Array<{ id: ElAbraTabId; label: string; searchPlaceholder: string }> = [
  { id: 'managements', label: 'Gerencias', searchPlaceholder: 'Buscar gerencia por nombre...' },
  {
    id: 'superintendencies',
    label: 'Superintendencias',
    searchPlaceholder: 'Buscar superintendencia por nombre...',
  },
  { id: 'areas', label: 'Áreas', searchPlaceholder: 'Buscar área o responsable...' },
  {
    id: 'groups',
    label: 'Grupos El Abra',
    searchPlaceholder: 'Buscar grupo, gerencia, superintendencia o área...',
  },
];

type PaginationState = {
  page: number;
  totalPages: number;
  totalCount: number;
  pageSize: number;
  hasNext: boolean;
  hasPrevious: boolean;
};

const emptyPagination = (): PaginationState => ({
  page: 1,
  totalPages: 1,
  totalCount: 0,
  pageSize: DEFAULT_PAGE_SIZE,
  hasNext: false,
  hasPrevious: false,
});

const parsePagination = (
  raw: unknown,
  fallbackPage: number,
  fallbackPageSize: number = DEFAULT_PAGE_SIZE
): PaginationState => {
  const p = (raw ?? {}) as Record<string, unknown>;
  return {
    page: Number(p.page) || fallbackPage,
    totalPages: Number(p.total_pages) || 1,
    totalCount: Number(p.total_count) || 0,
    pageSize: Number(p.page_size) || fallbackPageSize,
    hasNext: Boolean(p.has_next),
    hasPrevious: Boolean(p.has_previous),
  };
};

const byName = (a: { name: string }, b: { name: string }) =>
  String(a.name ?? '').localeCompare(String(b.name ?? ''), 'es', { sensitivity: 'base' });

const ElAbraCatalogPage: React.FC = () => {
  const [managements, setManagements] = useState<ManagementItem[]>([]);
  const [superintendencies, setSuperintendencies] = useState<SuperintendencyItem[]>([]);
  const [areas, setAreas] = useState<AreaItem[]>([]);
  const [elAbraGroups, setElAbraGroups] = useState<ElAbraGroupItem[]>([]);

  const [modalOptions, setModalOptions] = useState<{
    managements: ManagementItem[];
    superintendencies: SuperintendencyItem[];
    areas: AreaItem[];
  }>({ managements: [], superintendencies: [], areas: [] });

  const [modalKind, setModalKind] = useState<ElAbraModalKind | null>(null);
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<ElAbraGroupItem | null>(null);
  const [activeTab, setActiveTab] = useState<ElAbraTabId>('managements');
  const tabRefs = useRef<Partial<Record<ElAbraTabId, HTMLButtonElement | null>>>({});

  const [paginationByTab, setPaginationByTab] = useState<Record<ElAbraTabId, PaginationState>>({
    managements: emptyPagination(),
    superintendencies: emptyPagination(),
    areas: emptyPagination(),
    groups: emptyPagination(),
  });

  const [searchByTab, setSearchByTab] = useState<Record<ElAbraTabId, string>>({
    managements: '',
    superintendencies: '',
    areas: '',
    groups: '',
  });
  const searchByTabRef = useRef(searchByTab);
  searchByTabRef.current = searchByTab;
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pageSizeByTabRef = useRef<Record<ElAbraTabId, number>>({
    managements: DEFAULT_PAGE_SIZE,
    superintendencies: DEFAULT_PAGE_SIZE,
    areas: DEFAULT_PAGE_SIZE,
    groups: DEFAULT_PAGE_SIZE,
  });

  const {
    fetching,
    listManagements,
    listSuperintendencies,
    listAreas,
    storeManagement,
    storeSuperintendency,
    storeArea,
    listElAbraGroups,
    storeElAbraGroup,
    updateElAbraGroup,
  } = useAdicionalCatalogService();

  const setTabPagination = useCallback((tab: ElAbraTabId, pagination: PaginationState) => {
    pageSizeByTabRef.current[tab] = pagination.pageSize;
    setPaginationByTab(prev => ({ ...prev, [tab]: pagination }));
  }, []);

  const loadManagements = useCallback(
    (page: number = 1, search?: string, pageSizeParam?: number) => {
      const term = (search !== undefined ? search : searchByTabRef.current.managements).trim();
      const pageSize =
        pageSizeParam !== undefined ? pageSizeParam : pageSizeByTabRef.current.managements;
      if (pageSizeParam !== undefined) {
        pageSizeByTabRef.current.managements = pageSize;
      }
      listManagements(
        page,
        {
          onSuccess: (r: ServiceResponse) => {
            const data = r?.data;
            if (!data) return;
            const list = data.managements;
            if (Array.isArray(list)) setManagements(list);
            setTabPagination('managements', parsePagination(data.pagination, page, pageSize));
          },
        } as AdicionalEvents,
        term || undefined,
        pageSize
      );
    },
    [listManagements, setTabPagination]
  );

  const loadSuperintendencies = useCallback(
    (page: number = 1, search?: string, pageSizeParam?: number) => {
      const term = (
        search !== undefined ? search : searchByTabRef.current.superintendencies
      ).trim();
      const pageSize =
        pageSizeParam !== undefined
          ? pageSizeParam
          : pageSizeByTabRef.current.superintendencies;
      if (pageSizeParam !== undefined) {
        pageSizeByTabRef.current.superintendencies = pageSize;
      }
      listSuperintendencies(
        page,
        {
          onSuccess: (r: ServiceResponse) => {
            const data = r?.data;
            if (!data) return;
            const list = data.superintendencies;
            if (Array.isArray(list)) setSuperintendencies(list);
            setTabPagination(
              'superintendencies',
              parsePagination(data.pagination, page, pageSize)
            );
          },
        } as AdicionalEvents,
        term || undefined,
        pageSize
      );
    },
    [listSuperintendencies, setTabPagination]
  );

  const loadAreas = useCallback(
    (page: number = 1, search?: string, pageSizeParam?: number) => {
      const term = (search !== undefined ? search : searchByTabRef.current.areas).trim();
      const pageSize =
        pageSizeParam !== undefined ? pageSizeParam : pageSizeByTabRef.current.areas;
      if (pageSizeParam !== undefined) {
        pageSizeByTabRef.current.areas = pageSize;
      }
      listAreas(
        page,
        {
          onSuccess: (r: ServiceResponse) => {
            const data = r?.data;
            if (!data) return;
            const list = data.areas;
            if (Array.isArray(list)) setAreas(list);
            setTabPagination('areas', parsePagination(data.pagination, page, pageSize));
          },
        } as AdicionalEvents,
        term || undefined,
        pageSize
      );
    },
    [listAreas, setTabPagination]
  );

  const loadElAbraGroups = useCallback(
    (page: number = 1, search?: string, pageSizeParam?: number) => {
      const term = (search !== undefined ? search : searchByTabRef.current.groups).trim();
      const pageSize =
        pageSizeParam !== undefined ? pageSizeParam : pageSizeByTabRef.current.groups;
      if (pageSizeParam !== undefined) {
        pageSizeByTabRef.current.groups = pageSize;
      }
      listElAbraGroups(
        page,
        {
          onSuccess: (r: ServiceResponse) => {
            const data = r?.data;
            if (!data) return;
            const list = data.el_abra_groups;
            if (Array.isArray(list)) setElAbraGroups(list);
            setTabPagination('groups', parsePagination(data.pagination, page, pageSize));
          },
        } as AdicionalEvents,
        term || undefined,
        pageSize
      );
    },
    [listElAbraGroups, setTabPagination]
  );

  const loaders: Record<
    ElAbraTabId,
    (page?: number, search?: string, pageSize?: number) => void
  > = useMemo(
    () => ({
      managements: loadManagements,
      superintendencies: loadSuperintendencies,
      areas: loadAreas,
      groups: loadElAbraGroups,
    }),
    [loadManagements, loadSuperintendencies, loadAreas, loadElAbraGroups]
  );

  useEffect(() => {
    loadManagements(1);
    loadSuperintendencies(1);
    loadAreas(1);
    loadElAbraGroups(1);
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [loadManagements, loadSuperintendencies, loadAreas, loadElAbraGroups]);

  const handleSearchChange = useCallback(
    (tab: ElAbraTabId, value: string) => {
      setSearchByTab(prev => ({ ...prev, [tab]: value }));
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = setTimeout(() => {
        loaders[tab](1, value);
      }, SEARCH_DEBOUNCE_MS);
    },
    [loaders]
  );

  const loadModalOptions = useCallback(() => {
    listManagements(
      1,
      {
        onSuccess: (r: ServiceResponse) => {
          const list = r?.data?.managements;
          if (Array.isArray(list)) {
            setModalOptions(prev => ({ ...prev, managements: list }));
          }
        },
      } as AdicionalEvents,
      undefined,
      OPTIONS_PAGE_SIZE
    );
    listSuperintendencies(
      1,
      {
        onSuccess: (r: ServiceResponse) => {
          const list = r?.data?.superintendencies;
          if (Array.isArray(list)) {
            setModalOptions(prev => ({ ...prev, superintendencies: list }));
          }
        },
      } as AdicionalEvents,
      undefined,
      OPTIONS_PAGE_SIZE
    );
    listAreas(
      1,
      {
        onSuccess: (r: ServiceResponse) => {
          const list = r?.data?.areas;
          if (Array.isArray(list)) {
            setModalOptions(prev => ({ ...prev, areas: list }));
          }
        },
      } as AdicionalEvents,
      undefined,
      OPTIONS_PAGE_SIZE
    );
  }, [listManagements, listSuperintendencies, listAreas]);

  const sortedManagements = useMemo(() => [...managements].sort(byName), [managements]);
  const sortedSuperintendencies = useMemo(
    () => [...superintendencies].sort(byName),
    [superintendencies]
  );
  const sortedAreas = useMemo(() => [...areas].sort(byName), [areas]);
  const sortedGroups = useMemo(
    () =>
      [...elAbraGroups].sort((a, b) =>
        String(a.display_name ?? '').localeCompare(String(b.display_name ?? ''), 'es', {
          sensitivity: 'base',
        })
      ),
    [elAbraGroups]
  );

  const onStoreError = (r?: ServiceResponse) => {
    const errors = r?.errors;
    let firstFieldMsg: string | undefined;
    if (errors && typeof errors === 'object') {
      for (const value of Object.values(errors as Record<string, unknown>)) {
        const msg = Array.isArray(value) ? value[0] : value;
        if (typeof msg === 'string' && msg.trim()) {
          firstFieldMsg = msg.trim();
          break;
        }
      }
    }
    showToast(firstFieldMsg || r?.message || 'No se pudo crear el registro', 'error');
  };

  const handleModalSubmit = (payload: {
    name?: string;
    area_responsible?: string;
    description?: string;
  }) => {
    if (!modalKind) return;

    if (modalKind === 'management' && payload.name) {
      storeManagement(
        { name: payload.name },
        {
          onSuccess: () => {
            loadManagements(1, searchByTabRef.current.managements);
            setModalKind(null);
            showToast('Gerencia creada correctamente', 'success');
          },
          onError: onStoreError,
        }
      );
      return;
    }

    if (modalKind === 'superintendency' && payload.name) {
      storeSuperintendency(
        { name: payload.name },
        {
          onSuccess: () => {
            loadSuperintendencies(1, searchByTabRef.current.superintendencies);
            setModalKind(null);
            showToast('Superintendencia creada correctamente', 'success');
          },
          onError: onStoreError,
        }
      );
      return;
    }

    if (modalKind === 'area' && payload.name) {
      storeArea(
        { name: payload.name, area_responsible: payload.area_responsible },
        {
          onSuccess: () => {
            loadAreas(1, searchByTabRef.current.areas);
            setModalKind(null);
            showToast('Área creada correctamente', 'success');
          },
          onError: onStoreError,
        }
      );
    }
  };

  const handleGroupSubmit = (payload: {
    management: number;
    superintendency: number;
    area: number;
  }) => {
    const onSuccess = () => {
      loadElAbraGroups(1, searchByTabRef.current.groups);
      setGroupModalOpen(false);
      setEditingGroup(null);
      showToast(
        editingGroup ? 'Grupo El Abra actualizado correctamente' : 'Grupo El Abra creado correctamente',
        'success'
      );
    };
    const onError = () =>
      showToast(
        editingGroup ? 'No se pudo actualizar el grupo El Abra' : 'No se pudo crear el grupo El Abra',
        'error'
      );

    if (editingGroup) {
      updateElAbraGroup(editingGroup.id, payload, { onSuccess, onError });
      return;
    }

    storeElAbraGroup(payload, { onSuccess, onError });
  };

  const openGroupModal = () => {
    loadModalOptions();
    setEditingGroup(null);
    setGroupModalOpen(true);
  };

  const openEditGroupModal = (group: ElAbraGroupItem) => {
    loadModalOptions();
    setEditingGroup(group);
    setGroupModalOpen(true);
  };

  const modalLoading =
    (modalKind === 'management' && fetching.storeManagement) ||
    (modalKind === 'superintendency' && fetching.storeSuperintendency) ||
    (modalKind === 'area' && fetching.storeArea);

  const loadingLists =
    fetching.managements || fetching.superintendencies || fetching.areas || fetching.elAbraGroups;

  const focusTab = (id: ElAbraTabId) => {
    setActiveTab(id);
    tabRefs.current[id]?.focus();
  };

  const handleTabKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    const currentIndex = TABS.findIndex(t => t.id === activeTab);
    if (currentIndex === -1) return;

    if (e.key === 'ArrowRight') {
      e.preventDefault();
      focusTab(TABS[(currentIndex + 1) % TABS.length].id);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      focusTab(TABS[(currentIndex - 1 + TABS.length) % TABS.length].id);
    } else if (e.key === 'Home') {
      e.preventDefault();
      focusTab(TABS[0].id);
    } else if (e.key === 'End') {
      e.preventDefault();
      focusTab(TABS[TABS.length - 1].id);
    }
  };

  const activePagination = paginationByTab[activeTab];
  const activeSearch = searchByTab[activeTab];

  const tabCounts: Record<ElAbraTabId, number> = {
    managements: paginationByTab.managements.totalCount,
    superintendencies: paginationByTab.superintendencies.totalCount,
    areas: paginationByTab.areas.totalCount,
    groups: paginationByTab.groups.totalCount,
  };

  const renderToolbarAndFooter = (tab: ElAbraTabId, emptyLabel: string, table: React.ReactNode) => {
    const loadingKey =
      tab === 'managements'
        ? fetching.managements
        : tab === 'superintendencies'
          ? fetching.superintendencies
          : tab === 'areas'
            ? fetching.areas
            : fetching.elAbraGroups;
    const pagination = paginationByTab[tab];
    const hasRows =
      tab === 'managements'
        ? sortedManagements.length > 0
        : tab === 'superintendencies'
          ? sortedSuperintendencies.length > 0
          : tab === 'areas'
            ? sortedAreas.length > 0
            : sortedGroups.length > 0;
    const search = searchByTab[tab];
    const tabMeta = TABS.find(t => t.id === tab)!;

    return (
      <>
        <div className="el-abra-toolbar">
          <TableTextSearch
            value={search}
            onChange={value => handleSearchChange(tab, value)}
            placeholder={tabMeta.searchPlaceholder}
            aria-label={tabMeta.searchPlaceholder}
          />
        </div>
        {loadingKey ? (
          <div className="el-abra-loading">Cargando…</div>
        ) : !hasRows ? (
          <div className="el-abra-empty">
            {search.trim() ? `Sin resultados para “${search.trim()}”.` : emptyLabel}
          </div>
        ) : (
          table
        )}
        {pagination.totalCount > 0 ? (
          <div className="el-abra-footer">
            <Pagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              totalCount={pagination.totalCount}
              pageSize={pagination.pageSize}
              onPageChange={page => {
                loaders[tab](page, searchByTabRef.current[tab].trim() || undefined);
              }}
              onPageSizeChange={newSize => {
                loaders[tab](1, searchByTabRef.current[tab].trim() || undefined, newSize);
              }}
            />
          </div>
        ) : null}
      </>
    );
  };

  const renderManagements = () =>
    renderToolbarAndFooter(
      'managements',
      'No hay gerencias registradas.',
      <div className="el-abra-table-wrap">
        <table className="el-abra-table">
          <thead>
            <tr>
              <th>Nombre</th>
            </tr>
          </thead>
          <tbody>
            {sortedManagements.map(m => (
              <tr key={m.id}>
                <td>{m.name}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );

  const renderSuperintendencies = () =>
    renderToolbarAndFooter(
      'superintendencies',
      'No hay superintendencias registradas.',
      <div className="el-abra-table-wrap">
        <table className="el-abra-table">
          <thead>
            <tr>
              <th>Nombre</th>
            </tr>
          </thead>
          <tbody>
            {sortedSuperintendencies.map(s => (
              <tr key={s.id}>
                <td>{s.name}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );

  const renderAreas = () =>
    renderToolbarAndFooter(
      'areas',
      'No hay áreas registradas.',
      <div className="el-abra-table-wrap">
        <table className="el-abra-table">
          <thead>
            <tr>
              <th>Área</th>
              <th>Responsable</th>
            </tr>
          </thead>
          <tbody>
            {sortedAreas.map(a => (
              <tr key={a.id}>
                <td>{a.name}</td>
                <td>{a.area_responsible || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );

  const renderGroups = () =>
    renderToolbarAndFooter(
      'groups',
      'No hay grupos registrados.',
      <div className="el-abra-table-wrap">
        <table className="el-abra-table">
          <thead>
            <tr>
              <th>ID (Ger-Sup-Área)</th>
              <th>Grupo</th>
              <th>Gerencia</th>
              <th>Superintendencia</th>
              <th>Área</th>
              <th>Responsable</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {sortedGroups.map(g => (
              <tr key={g.id}>
                <td className="el-abra-group-ids">{`${g.management}-${g.superintendency}-${g.area}`}</td>
                <td>{g.display_name}</td>
                <td>{g.management_name}</td>
                <td>{g.superintendency_name}</td>
                <td>{g.area_name}</td>
                <td>{g.area_responsible || '—'}</td>
                <td>
                  <button
                    type="button"
                    className="el-abra-group-edit-btn"
                    onClick={() => openEditGroupModal(g)}
                    title="Modificar grupo"
                  >
                    <Pencil size={16} aria-hidden />
                    Modificar
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );

  const panelConfig: Record<
    ElAbraTabId,
    { title: string; addLabel: string; onAdd: () => void; render: () => React.ReactNode }
  > = {
    managements: {
      title: 'Gerencias',
      addLabel: 'Agregar gerencia',
      onAdd: () => setModalKind('management'),
      render: renderManagements,
    },
    superintendencies: {
      title: 'Superintendencias',
      addLabel: 'Agregar superintendencia',
      onAdd: () => setModalKind('superintendency'),
      render: renderSuperintendencies,
    },
    areas: {
      title: 'Áreas y responsables',
      addLabel: 'Agregar área',
      onAdd: () => setModalKind('area'),
      render: renderAreas,
    },
    groups: {
      title: 'Grupos El Abra (gerencia + superintendencia + área)',
      addLabel: 'Agregar grupo',
      onAdd: openGroupModal,
      render: renderGroups,
    },
  };

  const activePanel = panelConfig[activeTab];

  return (
    <div className="el-abra-page">
      <header className="el-abra-page-header">
        <h1>El Abra — Catálogos</h1>
        <p>
          Aquí se administran las <strong>gerencias</strong>, <strong>superintendencias</strong>,{' '}
          <strong>áreas</strong> (con responsable) y los <strong>grupos El Abra</strong> que agrupan esos
          datos para completar la pestaña Adicional en servicios de la empresa{' '}
          <strong>{ADICIONAL_COMPANY_NAME}</strong>.
        </p>
      </header>

      <div className="el-abra-tabs" role="tablist" aria-label="Catálogos El Abra">
        {TABS.map(tab => (
          <button
            key={tab.id}
            ref={el => {
              tabRefs.current[tab.id] = el;
            }}
            type="button"
            role="tab"
            id={`el-abra-tab-${tab.id}`}
            className={`el-abra-tab${activeTab === tab.id ? ' is-active' : ''}`}
            aria-selected={activeTab === tab.id}
            aria-controls={`el-abra-panel-${tab.id}`}
            tabIndex={activeTab === tab.id ? 0 : -1}
            onClick={() => setActiveTab(tab.id)}
            onKeyDown={handleTabKeyDown}
          >
            {tab.label}
            <span className="el-abra-tab-count">{tabCounts[tab.id]}</span>
          </button>
        ))}
      </div>

      <section
        className="el-abra-section"
        role="tabpanel"
        id={`el-abra-panel-${activeTab}`}
        aria-labelledby={`el-abra-tab-${activeTab}`}
      >
        <div className="el-abra-section-head">
          <div className="el-abra-section-head-text">
            <h2>{activePanel.title}</h2>
            <p className="el-abra-section-meta">
              {activePagination.totalCount} registro
              {activePagination.totalCount === 1 ? '' : 's'}
              {activeSearch.trim() ? ' filtrados' : ''}
            </p>
          </div>
          <button
            type="button"
            className="el-abra-add-btn"
            onClick={activePanel.onAdd}
            disabled={loadingLists}
          >
            {activePanel.addLabel}
          </button>
        </div>
        {activePanel.render()}
      </section>

      <AdicionalCatalogModal
        isOpen={modalKind !== null}
        onClose={() => setModalKind(null)}
        kind={modalKind ?? 'management'}
        onSubmit={handleModalSubmit}
        loading={modalLoading}
      />

      <ElAbraGroupModal
        isOpen={groupModalOpen}
        onClose={() => {
          setGroupModalOpen(false);
          setEditingGroup(null);
        }}
        onSubmit={handleGroupSubmit}
        loading={fetching.storeElAbraGroup || fetching.updateElAbraGroup}
        managements={modalOptions.managements}
        superintendencies={modalOptions.superintendencies}
        areas={modalOptions.areas}
        initialValues={editingGroup}
        title={editingGroup ? 'Modificar grupo El Abra' : 'Nuevo grupo El Abra'}
        submitLabel={editingGroup ? 'Guardar cambios' : 'Guardar grupo'}
      />
    </div>
  );
};

export default ElAbraCatalogPage;
